import React, { useEffect, useState } from "react";
import {
  BuildingOffice2Icon,
  CalendarDaysIcon,
  EnvelopeIcon,
  MapPinIcon,
  PhoneIcon,
  UserCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Spinner } from "@material-tailwind/react";
import { portalApi } from "../../services/portalApi";
import { labelAppointmentModality } from "../../utils/consultationModes";

function fullName(c) {
  if (!c) return "—";
  return [c.firstName, c.lastName].filter(Boolean).join(" ").trim() || "—";
}

function companyLabel(c) {
  if (!c) return "—";
  return (
    String(c.companyEmployerName || c.company || "").trim() || "Sin empresa"
  );
}

function formatApptDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-MX", {
    timeZone: "America/Mexico_City",
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function formatCreatedAt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX", {
      timeZone: "America/Mexico_City",
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function statusLabel(status) {
  if (status === "scheduled") return "Programada";
  if (status === "cancelled") return "Cancelada";
  return status || "—";
}

function statusBadgeClass(status) {
  if (status === "scheduled") {
    return "bg-emerald-50 text-emerald-800 border-emerald-200";
  }
  if (status === "cancelled") {
    return "bg-red-50 text-red-700 border-red-200";
  }
  return "bg-brand-lilac/40 text-brand-navy border-brand-lavender/30";
}

function DetailField({ icon: Icon, label, children }) {
  return (
    <div className="min-w-0 space-y-1">
      <div className="portal-label flex items-center gap-1.5">
        {Icon ? <Icon className="h-4 w-4 shrink-0 text-brand-gray" aria-hidden /> : null}
        {label}
      </div>
      <div className="portal-value break-words">{children}</div>
    </div>
  );
}

/**
 * Drawer lateral con detalle de cliente + citas agendadas.
 */
export default function AdminClientDetailDrawer({
  client: initialClient,
  open,
  onClose,
}) {
  const [client, setClient] = useState(initialClient);
  const [appointments, setAppointments] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingAppts, setLoadingAppts] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setClient(initialClient);
  }, [initialClient]);

  useEffect(() => {
    if (!open || !initialClient?.userId) return undefined;

    let cancelled = false;
    setError("");
    setLoadingDetail(true);
    setLoadingAppts(true);

    portalApi
      .adminClient(initialClient.userId)
      .then((r) => {
        if (!cancelled && r?.client) setClient(r.client);
      })
      .catch(() => {
        /* usamos el row inicial */
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });

    portalApi
      .adminClientAppointments(initialClient.userId)
      .then((r) => {
        if (!cancelled) setAppointments(r.appointments || []);
      })
      .catch((e) => {
        if (!cancelled) {
          setAppointments([]);
          setError(e.message || "No se pudieron cargar las citas");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingAppts(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, initialClient?.userId]);

  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open || !client) return null;

  const name = fullName(client);

  return (
    <div className="fixed inset-0 z-[100]" role="dialog" aria-modal="true" aria-label={`Detalle de ${name}`}>
      <button
        type="button"
        className="absolute inset-0 bg-brand-navy/40 border-0 cursor-default"
        aria-label="Cerrar panel"
        onClick={onClose}
      />
      <aside className="absolute top-0 right-0 bottom-0 flex h-full w-full max-w-md sm:max-w-lg flex-col overflow-hidden bg-brand-navy shadow-2xl animate-[slideInRight_0.2s_ease-out]">
        <div className="shrink-0 bg-brand-navy text-white px-5 pt-5 pb-6">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="portal-meta !text-white/70 mb-1">
                Usuario · {String(client.userId || "").slice(0, 8)}
              </p>
              <h2 className="text-[1.375rem] sm:text-[1.5rem] font-semibold leading-tight truncate">
                {name}
              </h2>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {client.state ? (
                  <span className="inline-flex rounded-full bg-white/15 px-2.5 py-1 text-[0.8125rem] font-semibold">
                    {client.state}
                  </span>
                ) : null}
                <span className="inline-flex rounded-full bg-brand-lavender/90 px-2.5 py-1 text-[0.8125rem] font-semibold">
                  Cliente
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-white/80 hover:bg-white/10 hover:text-white transition-colors"
              aria-label="Cerrar"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto bg-white">
          {loadingDetail ? (
            <div className="flex items-center justify-center gap-2 py-10 portal-meta">
              <Spinner className="h-5 w-5" />
              Cargando perfil…
            </div>
          ) : (
            <div className="px-5 py-5 border-b border-brand-lavender/15">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <DetailField icon={EnvelopeIcon} label="Correo">
                  {client.email ? (
                    <a
                      href={`mailto:${client.email}`}
                      className="text-brand-lavender font-normal hover:underline break-all"
                    >
                      {client.email}
                    </a>
                  ) : (
                    "—"
                  )}
                </DetailField>
                <DetailField icon={PhoneIcon} label="Teléfono">
                  {client.phone ? (
                    <a
                      href={`tel:${String(client.phone).replace(/\s/g, "")}`}
                      className="hover:underline font-normal"
                    >
                      {client.phone}
                    </a>
                  ) : (
                    "—"
                  )}
                </DetailField>
                <DetailField icon={MapPinIcon} label="Estado">
                  {client.state || "—"}
                </DetailField>
                <DetailField icon={BuildingOffice2Icon} label="Empresa">
                  {companyLabel(client)}
                </DetailField>
                <DetailField icon={BuildingOffice2Icon} label="Cuenta / subempresa">
                  {client.companyAccountName || "—"}
                </DetailField>
                <DetailField icon={CalendarDaysIcon} label="Registro">
                  {formatCreatedAt(client.createdAt)}
                </DetailField>
              </div>
              {client.profileImageUrl ? (
                <div className="mt-6 flex items-center gap-3">
                  <img
                    src={client.profileImageUrl}
                    alt=""
                    className="h-14 w-14 rounded-full object-cover border border-brand-lavender/20"
                  />
                  <p className="portal-meta">Foto de perfil</p>
                </div>
              ) : (
                <div className="mt-6 flex items-center gap-3 text-brand-gray">
                  <UserCircleIcon className="h-14 w-14" aria-hidden />
                  <p className="portal-meta">Sin foto de perfil</p>
                </div>
              )}
            </div>
          )}

          <div className="px-5 py-5">
            <div className="flex items-baseline justify-between gap-2 mb-4">
              <h3 className="portal-section-title">Citas agendadas</h3>
              {!loadingAppts ? (
                <span className="portal-meta tabular-nums">{appointments.length}</span>
              ) : null}
            </div>

            {loadingAppts ? (
              <div className="flex items-center justify-center gap-2 py-8 portal-meta">
                <Spinner className="h-5 w-5" />
                Cargando citas…
              </div>
            ) : error ? (
              <p className="portal-value !text-red-600 py-4">{error}</p>
            ) : appointments.length === 0 ? (
              <p className="portal-meta py-6 text-center border border-dashed border-brand-lavender/25 rounded-xl">
                Este usuario aún no tiene citas registradas.
              </p>
            ) : (
              <ul className="space-y-3">
                {appointments.map((a) => (
                  <li
                    key={a.appointmentId}
                    className="rounded-xl border border-brand-lavender/20 bg-white p-4 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="min-w-0">
                        <p className="portal-label truncate">
                          {a.psychologistName || "Psicólogo"}
                        </p>
                        <p className="portal-meta mt-1">
                          {formatApptDateTime(a.startUtc)}
                        </p>
                      </div>
                      <span
                        className={`shrink-0 inline-flex rounded-full border px-2.5 py-1 text-[0.8125rem] font-semibold ${statusBadgeClass(
                          a.status
                        )}`}
                      >
                        {statusLabel(a.status)}
                      </span>
                    </div>
                    <div className="space-y-1.5">
                      <p className="portal-meta">
                        <span className="portal-label mr-1">Modalidad</span>
                        <span className="portal-value">
                          {a.modality
                            ? labelAppointmentModality(a.modality)
                            : "—"}
                        </span>
                      </p>
                      {a.psychologistPhone ? (
                        <p className="portal-meta">
                          <span className="portal-label mr-1">Tel. psicólogo</span>
                          <a
                            href={`tel:${String(a.psychologistPhone).replace(/\s/g, "")}`}
                            className="portal-value text-brand-lavender hover:underline"
                          >
                            {a.psychologistPhone}
                          </a>
                        </p>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </aside>
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
